import type { BorrowerPlatformStateType } from "@/enums/bpCustomisation";
import type { CommunicationType } from "@/enums/communicationLogs";
import type { Entity } from "@/enums/entityProfiles";
import type {
  NotificationGroupType,
  WorkflowWebhookParameterType,
} from "@/enums/workflows";
import type {
  CONTACT_METHODS,
  INVOICING_METHODS,
  SUBMISSION_TYPES,
} from "@/helpers/constants";
import type { AFFILIATED_INDIVIDUAL_FIELD_IDS } from "@/helpers/constants/workflow";
import type {
  DebtSchedule,
  IBankAccountDetail,
  IBillingInformation,
  IDebt,
  IOfficer,
} from "@/models/applications";
import type {
  IClient,
  IEmailCustomization,
  IWhitelabel,
} from "@/models/clients";
import type {
  Booleanish,
  FinProductNum,
  IAddress,
  IResponseLinks,
  IResponseMeta,
  Identifiable,
} from "@/models/common";
import type { IFunder } from "@/models/funders";
import type {
  BillingType,
  CheckoutApplicationFieldType,
  RiskTierKey,
  Servicer,
} from "@/models/options";
import type { IOrchestrationTemplate } from "@/models/orchestration";
import type { IWidgetTemplate } from "@/models/widgetTemplates";
import type { BrandName } from "@/schemas/brandName";

export type WorkflowCategories = 1 | 2 | 3 | 4 | 5;

export interface WidgetDefinition {
  id: number;
  title: string;
  is_default_destination_borrower_platform: boolean;
}

export type ButtonRedirectType = "resume_link" | "redirect_link";
export interface BorrowerPlatformState {
  type: BorrowerPlatformStateType;
  title: string;
  description: string;
  show_button: boolean;
  button_text: string;
  button_url: string;
  button_redirect_type: ButtonRedirectType | null;
  resume_product_type?: number;
  blocks: string[];
}

export interface BorrowerPlatformStage {
  id: string;
  name: string;
  description: string;
  workflow_template_stages: IWorkflow["id"][];
  states: BorrowerPlatformState[];
}

export interface BorrowerPlatformAiAgentSettings {
  agent_id: string;
  is_enabled: boolean;
}

export interface BorrowerPlatformOptions {
  is_enabled: boolean;
  mask_funder_details: boolean;
  allows_acceptance_of_multiple_offers: boolean;
  offer_display: number;
  offer_declined_reasons: string[];
  offer_declined_reasons_other_label: string | null;
  default_borrower_platform_template_id?: string;
  stages: BorrowerPlatformStage[];
  enable_checkout: boolean;
  chat: BorrowerPlatformAiAgentSettings;
}

export interface WorkflowDocuments {
  sync: {
    down: {
      enabled: boolean;
    };
  };
}

export interface IWorkflow extends Identifiable {
  billing_type?: BillingType;
  brand_name_id?: BrandName["id"] | null;
  category?: IWorkflowCategory;
  category_id?: WorkflowCategories;
  client?: IClient | null;
  client_id?: IClient["id"] | null;
  content?: WorkflowContent;
  created_at: string;
  description: string | null;
  domain?: IWhitelabel | null;
  email_branding?: IEmailCustomization | null;
  email_definitions: EmailDefinition[];
  flow_id?: string;
  id: string;
  is_active: boolean;
  is_default: boolean;
  is_global: boolean;
  is_manually_selecting_offers: boolean;
  sync_older_deals: boolean;
  managed_by_lendflow: boolean;
  name: string;
  offer_declined_reasons: string[];
  offer_declined_reasons_other_label: string | null;
  product_types?: Record<string, WidgetDefinition>;
  sdk_branding?: IWidgetTemplate | null;
  short_id?: string;
  sms_definitions: SmsDefinition[];
  stage_category?: StageCategoryType;
  stage_id?: string;
  stage_name?: string;
  status: number | null;
  type: WorkflowType;
  updated_at: string;
  widget: number[] | null;
  workflow_segment: string;
}

export interface WorkflowContent {
  activity_hub: ActivityHubOptions;
  borrower_platform: BorrowerPlatformOptions;
  documents: WorkflowDocuments;
  email_submission: EmailSubmission;
  emails: Array<NotificationItem>;
  fast_track: FastTrack;
  flow: IFlowRecord[];
  sms_notifications: Array<NotificationItem>;
  stages: IWorkflowStage[];
  statuses?: IWorkflowStatus[];
  webhooks: Array<WorkflowWebhook>;
  lendflow_webhooks: Array<WorkflowWebhook>;
}

export interface FastTrack {
  underwriting?: FastTrackUnderwriting;
  placements?: Array<FastTrackAutomationPlacement>;
  transitions?: Array<FastTrackTransition>;
  widget?: FastTrackWidget;
}

export interface EmailSubmission {
  is_enabled: boolean;
  whitelabel_id: string | null;
  branding_id: number | null;
  template_id: number | null;
}

export interface FastTrackWidgetData {
  to: IWorkflowStage["id"];
  placements?: Array<FastTrackPlacement>;
}

export interface FastTrackWidget {
  drop_off?: FastTrackWidgetData;
  finished?: FastTrackWidgetData;
}

export interface FastTrackTransition {
  from: IWorkflowStage["id"];
  to: IWorkflowStage["id"];
  placements: Array<FastTrackPlacement>;
}

export interface FastTrackPlacement {
  funder: IFunder["id"];
  method: Lowercase<(typeof SUBMISSION_TYPES)[keyof typeof SUBMISSION_TYPES]>;
  offer_template?: string;
  destinations?: OfferDestinationRecord[];
  workflow_template: string;
  product?: FinProduct;
}

export interface FastTrackAutomationPlacement {
  product: Exclude<FinProduct, "erc">;
  risk_tiers: RiskTierKey[];
  max_placements: number | null;
  conditions: Array<{ comparator: string; score_card_value: number | null }>;
}

export type RequirementType = "bank_data" | "signed_document";

export interface FastTrackAutomationRequirement {
  type: RequirementType;
  minimum_owners?: "all" | "some";
  minimum_statements?: number;
}

export interface FastTrackUnderwriting {
  enabled: boolean;
  placements?: Array<FastTrackAutomationPlacement>;
  requirements?: Array<FastTrackAutomationRequirement>;
}

export interface WorkflowWebhook {
  type: WorkflowWebhookDefinitionItem["type"];
  enabled: boolean;
  stage_id?: IWorkflowStage["id"];
  name?: string;
  urls: Array<string>;
  id?: string;
  parameters?: WorkflowWebhookParameter[];
}

export type CreateWorkflowTemplate = Partial<IWorkflow> & {
  client_id?: string | null;
};

export type StageCategoryType =
  | "Collect Data"
  | "Data Enrichment"
  | "Scorecards"
  | "Placement"
  | "Offer"
  | "Closing"
  | "Complete";

export interface IFlowRecord {
  id: string;
  name: string;
  stage_id: string;
}

export interface IWorkflowCategory {
  id: number;
  name: string;
  description: string;
}

export type FinProduct =
  | "arloc"
  | "line_of_credit"
  | "receivables_purchase"
  | "term_loan"
  | "equipment_financing"
  | "erc";

export interface FinProductRecord {
  type: FinProduct;
  enabled: boolean;
  stips?: number[];
  funders?: string[];
}

export interface OfferDestinationRecord {
  offer_template?: string | null;
  workflow_template?: string | null;
}

export interface WfbOfferTemplateRecord {
  id: string;
  weight?: number;
  enabled: boolean;
  stips?: number[];
  destinations: OfferDestinationRecord[];
}
export interface WfbFunderRecord {
  id: string;
  offer_templates: WfbOfferTemplateRecord[];
  stips?: number[];
}

export interface IWorkflowStage {
  id: string;
  type: ApplicationStage;
  name: string;
  management: IWorkflowManagement;
  statuses: IWorkflowStatus[];
  tabs?: IWorkflowTab[];
  cards?: string[];
  groups?: string[];
  funders?: WfbFunderRecord[] | string[];
  products?: FinProductRecord[];
  primary_card?: string;
  primary_group?: string;
  data_orchestration_templates?: Array<IOrchestrationTemplate["id"]>;
  data_orchestration_template_automation?: IOrchestrationTemplate["id"] | null;
  includes_checkout_data?: boolean;
}

export interface IWorkflowTab {
  name: string;
  blocks: IWorkflowBlock[];
  entity_type?: Entity;
}

export interface IWorkflowBlock {
  id: WorkflowBlockIds;
  name?: string;
  fields?: IWorkflowField[];
  entity_type?: Entity;
  icon: string;
  group?: BlockGroup;
  related_services: string[];
  servicers?: Record<"type", Servicer>[];
  custom_attributes?: string[];
}

export interface IDiscoveryQuestion {
  id: string;
  question: string;
  description: string | null;
}

export interface IDiscoveryWorkflowBlock extends IWorkflowBlock {
  questions: IDiscoveryQuestion[];
}

export interface IStipsWorkflowBlock extends IWorkflowBlock {
  stips: Array<{ id: number }>;
}

export interface IWorkflowField {
  id: string;
  required: boolean;
  signed_application: boolean;
  hidden: boolean;
}

export interface IWorkflowManagement {
  lendflow: boolean;
  client: boolean;
}

export const LIVE_STATUS_TYPE = "live";
export const DEAD_STATUS_TYPE = "dead";

export type StatusType = typeof LIVE_STATUS_TYPE | typeof DEAD_STATUS_TYPE;

export interface IWorkflowStatus {
  name: string;
  live: boolean;
  color?: string;
  substatuses?: string[];
  substatuses_weight?: IWorkflowSubStatusWeight[];
}

export interface IWorkflowSubStatusWeight {
  name: string;
  weight: number;
}
export interface IWorkflowStageTypeInfo {
  title: string;
  description: string;
  icon: string;
  type: ApplicationStage;
}

export interface IWorkflowTemplatesState {
  templatesState: IWorkflowData;
  activeTemplate: IWorkflow;
  activeStage: IWorkflowStage;
  activeTab: IWorkflowTab;
  activeEntityType: Entity;
  templateFields: WorkflowBlocksData;
  activeOwner: string | null;
  showStatusesSidebar: boolean;
  newStageIds: Array<IWorkflowStage["id"]>;
  /**
   * @description Inactive states are not added to new stages.
   */
  inactiveStates: BorrowerPlatformStateType[];
  selectedBorrowerPlatformStageId: BorrowerPlatformStage["id"];
  selectedBorrowerPlatformState: BorrowerPlatformState["type"] | null;
}

export interface IWorkflowData {
  data: IWorkflow[];
  links: IResponseLinks;
  meta: IResponseMeta;
  loading: boolean;
}

export interface WorkflowFieldDefinition {
  id: string;
  cannot_be_hidden: boolean;
  description: string | null;
  editable: boolean;
  name: string;
  readonly: boolean;
  field_type?: CheckoutApplicationFieldType;
  options_key?: string;
}
export interface WorkflowBlockDefinition {
  id: WorkflowBlockIds;
  name: string;
  icon: string | null;
  group: string;
  description: string | null;
  fields: WorkflowFieldDefinition[];
}

export interface WorkflowStageDefinition {
  blocks: WorkflowBlockDefinition[];
  description: string;
  icon: string;
  title: string;
  type: string;
}

export interface WorkflowWebhookDefinitionItem {
  name: string;
  // There is a corresponding enum on the API for the "type" property
  // but in the moment of writing I didn't notice that FE has to be aware
  // of the webhook type, so it's represented as a readonly string.
  readonly type: string;
  stage?: ApplicationStage;
  description: string | null;
  urls?: string[];
  enabled?: boolean;
  parameters?: WorkflowWebhookParameter[];
}
export interface WorkflowWebhookDefinition {
  description: string | null;
  name: string;
  webhooks: Array<WorkflowWebhookDefinitionItem>;
}

export interface BorrowerPlatformStateDefinition {
  default_description: string;
  default_title: string;
  name: string;
  type: BorrowerPlatformStateType;
  workflow_template_stage_types: string[];
  blocks: string[];
}

export interface BorrowerPlatformDefinition {
  button_redirect_types: Record<ButtonRedirectType, string>;
  states: BorrowerPlatformStateDefinition[];
  resume_widgets: WidgetDefinition[];
}

export type IWorkflowTemplateDefinition = {
  modes: WorkflowType[];
  stages: WorkflowStageDefinition[];
  webhooks: Array<WorkflowWebhookDefinition>;
  emails: EmailNotification[];
  borrower_platform: BorrowerPlatformDefinition;
};

export interface WorkflowTemplateCategoriesDefinition {
  id: number;
  name: string;
  description: string;
}

export interface IWorkflowFieldDescription extends IWorkflowField {
  description: string | null;
  editable: boolean;
  readonly: boolean;
  name: string;
}

export type WorkflowType = "linear" | "dynamic";

export interface DeleteRecord {
  id: string | number;
  _delete: boolean;
}

export interface IWorkflowStageOption {
  type: ApplicationStage;
  title: string;
  icon: string;
  description: string;
  blocks: IWorkflowBlock[];
  statuses: IWorkflowStatus[];
}
export interface AdditionalAffiliatedBusinessInfoFields {
  affiliated_businesses_additional_information_id: number;
  affiliated_businesses_additional_information_fiscal_year_end: string;
  affiliated_businesses_additional_information_industry: number;
  affiliated_businesses_additional_information_sub_industry: number | null;
  affiliated_businesses_additional_information_SIC_code: string;
  affiliated_businesses_additional_information_NAICS_code: string;
  affiliated_businesses_additional_information_experian_bin: number;
  affiliated_businesses_additional_information_duns_number: number;
  affiliated_businesses_additional_information_website: string;
  affiliated_businesses_additional_information_number_of_employees: string;
  affiliated_businesses_additional_information_business_management_software: string;
  affiliated_businesses_additional_information_industry_other: string;
  affiliated_businesses_additional_information_w2_number_of_employees_2020: string;
  affiliated_businesses_additional_information_w2_number_of_employees_2021: string;
  affiliated_businesses_additional_information_current_business_debt:
    | number
    | null;
  affiliated_businesses_additional_information_custom_field: string;
  affiliated_businesses_additional_information_dot_number: string | null;
}

export interface AdditionalBusinessInfoFields {
  additional_business_NAICS_code: string | null;
  additional_business_SIC_code: string | null;
  additional_business_business_management_software: string | null;
  additional_business_custom_field: string | null;
  additional_business_dot_number: number | null;
  additional_business_duns_number: number | null;
  additional_business_experian_bin: number | null;
  additional_business_fiscal_year_end: string | null;
  additional_business_industry: number | null;
  additional_business_industry_other: string | null;
  additional_business_number_of_employees: number | null;
  additional_business_sub_industry: number | null;
  additional_business_w2_number_of_employees_2020: string | null;
  additional_business_w2_number_of_employees_2021: string | null;
  additional_business_current_business_debt: number | null;
  additional_business_website: string | null;
  additional_business_stated_average_balance: number | null;
  additional_business_external_NAICS_description: string | null;
  additional_business_external_SIC_description: string | null;
  additional_business_operating_expense: number | null;
  additional_business_stated_annual_revenue: number | null;
  additional_business_business_description: string | null;
  additional_business_business_experience: string | null;
  additional_business_incomplete_email: string | null;
  additional_business_incomplete_phone: string | null;
  additional_business_stated_monthly_revenue: number | null;
  additional_business_use_of_funds: string | null;
  additional_business_use_of_funds_description: string | null;
  additional_business_stated_industry: string | null;
  additional_business_written_business_plan: string | null;
  additional_business_financial_projections: string | null;
  additional_business_generating_revenue: string | null;
  additional_business_veteran: string | null;
  additional_business_franchise: string | null;
  additional_business_stated_number_of_employees: string | null;
  additional_business_involved_in_bankruptcy: string | null;
  additional_business_baselayer_business_id: string | null;
  additional_business_baselayer_search_id: string | null;
}
export interface AffiliatedBusinessInfoFields {
  affiliated_business_id: number;
  affiliated_businesses_addresses: Partial<IAddress | DeleteRecord>[];
  affiliated_businesses_dba: string;
  affiliated_businesses_ein: string;
  affiliated_businesses_entity_type: string;
  affiliated_businesses_legal_name: string;
  affiliated_businesses_phone_numbers: PhoneNumber[];
  affiliated_businesses_start_date: string;
  affiliated_businesses_state_of_incorporation: string;
  affiliated_businesses_stated_monthly_revenue: number;
}

export type IWFLAffiliatedBusiness = {
  affiliated_business_id: number;
  affiliated_businesses_legal_name: string | null;
};

export type IWFLAffiliatedIndividual = {
  affiliated_individual_id: number;
  affiliated_individuals_first_name: string | null;
  affiliated_individuals_last_name: string | null;
  affiliated_individuals_drivers_license_number: string | null;
  affiliated_business_id: number | null;
  affiliated_individuals_drivers_license_state: string | null;
  affiliated_individuals_date_of_birth: string | null;
  affiliated_individuals_telephone: {
    telephone: string | null;
    additional_telephones: string[];
  };
  affiliated_individuals_email_address: {
    email_address: string | null;
    additional_emails: string[];
  };
  affiliated_individuals_ssn_itin: {
    itin: string | null;
    ssn: string | null;
  };
  affiliated_individuals_addresses: IAddress[] | [];
};

export type IWFLAffiliatedIndividualAdditional = {
  affiliated_individuals_additional_information_id: number;
  affiliated_individuals_additional_information_credit_permission:
    | number
    | null;
  affiliated_individuals_additional_information_total_debt: number | null;
  affiliated_individuals_additional_information_credit_limit: number | null;
  affiliated_individuals_additional_information_stated_credit_score:
    | string
    | null;
  affiliated_individuals_additional_information_actual_credit_score:
    | string
    | null;
  affiliated_individuals_additional_information_credit_utilization:
    | number
    | null;
  affiliated_individuals_additional_information_inquiries_6_months:
    | number
    | null;
  affiliated_individuals_additional_information_personal_income: number | null;
  affiliated_individuals_additional_information_legal_immigration_status:
    | number
    | null;
  affiliated_individuals_additional_information_primary_language_spoken:
    | number
    | null;
};

export type TradeReference = {
  trade_reference_id: number;
  trade_references_business_name: string | null;
  trade_references_business_telephone: string | null;
  trade_references_business_email: string | null;
  trade_references_business_address: IAddress;
  trade_references_business_contact_first_name: string | null;
  trade_references_business_contact_last_name: string | null;
  trade_references_credit_terms: string | null;
  trade_references_date_account_opened: string | null;
  trade_references_open_ar_balance: string | null;
  trade_references_past_due_balance: string | null;
  trade_references_highest_previous_balance: string | null;
  trade_references_days_beyond_terms: string | null;
  trade_references_number_of_late_payments: string | null;
  trade_references_credit_limit: string | null;
  trade_references_number_of_credit_transactions: string | null;
  trade_references_date_of_last_transaction: string | null;
};

export type IWorkflowLiveDebtor = {
  debtor_id: number | string;
  customer_information_business_name: string | null;
  customer_information_amount: number | string | null;
  customer_information_payment_term: number | string | null;
};

export type ApplicationStage =
  | "application"
  | "underwriting"
  | "placement"
  | "offer"
  | "closing"
  | "funded"
  | "scorecards";

export type DoStatus =
  | "pass"
  | "fail"
  | "manual_review"
  | "missing_data"
  | "error";

export type BlockGroup =
  | "Prequal"
  | "Business Information"
  | "Personal Information"
  | "Signed Application"
  | "Bank Data"
  | "Discovery"
  | "KYC"
  | "KYB"
  | "Cash Flow Analysis"
  | "Liens and More"
  | "Document Verification"
  | "Business Credit"
  | "Social Scoring";

// Please keep the keys sorted alphabetically
export type WorkflowBlockIds =
  | "accounts_payable"
  | "additional_business"
  | "additional_owner"
  | "affiliated_businesses"
  | "affiliated_businesses_additional_information"
  | "affiliated_individuals"
  | "affiliated_individuals_additional_information"
  | "bank_account_data"
  | "bank_account_details"
  | "bank_data"
  | "bank_data_moneythumb"
  | "bank_data_plaid"
  | "bank_data_ocrolus"
  | "baselayer_kyb"
  | "bbb_kyb"
  | "blu_cognition"
  | "bonding_agent"
  | "business"
  | "business_debt_schedule"
  | "business_extra"
  | "business_notes"
  | "business_tax_return_information"
  | "business_transportation_information"
  | "checkout"
  | "clear_adverse_media"
  | "clear_bankruptcies"
  | "clear_business_litigation"
  | "clear_business_match"
  | "clear_business_uccs"
  | "clear_court_search"
  | "clear_individual_litigation"
  | "clear_individual_match"
  | "clear_individual_uccs"
  | "clear_kyb"
  | "clear_kyc"
  | "clear_liens"
  | "clear_liens_business"
  | "clear_liens_individual"
  | "codat"
  | "company_debts"
  | "company_officers"
  | "custom_attributes"
  | "customer_information"
  | "data_merch_kyb"
  | "discovery"
  | "dnb_business_credit"
  | "dnb_business_match"
  | "dnb_company_information_kyb"
  | "document_data_extraction"
  | "ekata"
  | "enigma_kyb"
  | "enigma_business_match"
  | "arsen_ai_enrich_match"
  | "equipment_details"
  | "equifax_business_credit"
  | "equifax_kyb"
  | "equifax_sbfe_report_business_credit"
  | "equifax_cfn_report_business_credit"
  | "experian_bankruptcies"
  | "experian_business_contacts"
  | "experian_credit_status"
  | "experian_corporate_registrations"
  | "experian_legal_collections"
  | "experian_business_credit"
  | "experian_business_match"
  | "experian_business_quick_search"
  | "experian_gdn_business_credit"
  | "experian_judgments"
  | "experian_kyb"
  | "experian_liens"
  | "experian_personal_credit"
  | "experian_commercial_collections"
  | "experian_uccs"
  | "experian_fraud"
  | "fmcsa_kyb"
  | "foursquare_kyb"
  | "funding"
  | "funding_details"
  | "funding_summary"
  | "gbp_kyb"
  | "guarantor"
  | "inscribe"
  | "insurance"
  | "lexis_nexis_business_credit"
  | "lexisnexis_bankruptcies"
  | "lexisnexis_contact_card_report"
  | "lexisnexis_email_search"
  | "lexisnexis_judgments"
  | "lexisnexis_kyb"
  | "lexisnexis_kyc"
  | "lexisnexis_liens"
  | "lexisnexis_personal_credit"
  | "lexisnexis_uccs"
  | "loan_servicing"
  | "manta_kyb"
  | "middesk"
  | "middesk_bankruptcies"
  | "middesk_business_search"
  | "middesk_kyb"
  | "middesk_liens"
  | "moneythumb"
  | "monthly_balance"
  | "numverify"
  | "opencorporates_company_search"
  | "opencorporates_kyb"
  | "opencorporates_kyc"
  | "ocrolus"
  | "ocrolus_doc_verification"
  | "offers"
  | "offer_details"
  | "persona"
  | "personal"
  | "personal_debt_schedule"
  | "personal_tax_return_information"
  | "plaid"
  | "prequal"
  | "prismdata"
  | "sentilink_fraud"
  | "sentilink_match"
  | "signed_application"
  | "spinwheel_personal_credit"
  | "socure_fraud"
  | "socure_kyc"
  | "stips"
  | "tax_status_kyb"
  | "tax_status_kyc"
  | "trade_references"
  | "transactions"
  | "transunion_personal_credit"
  | "tripadvisor_kyb"
  | "chart_tax_data"
  | "twilio_lookup"
  | "vouched"
  | "yellow_pages_kyb"
  | "yelp_kyb";

export type FieldOption = "required" | "hidden" | "signed_application";

export interface OwnerWFL {
  id?: string;
  personal_addresses: (IAddress | DeleteRecord)[];
  personal_date_of_birth: string;
  personal_drivers_license_number: string;
  personal_drivers_license_state: string;
  personal_email_address: {
    email_address: string;
    additional_emails: string[];
  };
  personal_first_name: string;
  personal_information_id: number;
  personal_is_primary: boolean;
  personal_last_name: string;
  personal_ownership_percentage: string;
  personal_ssn_itin: {
    itin: string;
    ssn: string;
  };
  personal_sin: string | null;
  personal_telephone: {
    telephone: string;
    additional_telephones: string[];
  };
  personal_phone_numbers?: PhoneNumber[];
}

export interface BusinessInfoFields {
  business_address: Partial<IAddress>[];
  business_dba: string;
  business_ein: string;
  business_entity_type: string;
  business_legal_name: string;
  business_phone_numbers: string[];
  business_start_date: string;
  business_state_of_incorporation: string;
}

export interface OwnerAdditionalInfo {
  additional_owner_id?: number | string;
  additional_owner_actual_credit_score: number | string;
  additional_owner_credit_limit: number | string;
  additional_owner_credit_permission: number | boolean;
  additional_owner_credit_utilization: number | string;
  additional_owner_inquiries_6_months: number | string;
  additional_owner_legal_immigration_status: string;
  additional_owner_personal_income: number | string;
  additional_owner_primary_language_spoken: number | string;
  additional_owner_stated_credit_score: string;
  additional_owner_total_debt: number | string;
  additional_owner_country_of_citizenship: string;
}

export interface PhoneNumber {
  id?: number | string;
  phone_number?: string;
  is_primary?: boolean;
  line_type?: string;
  _delete?: boolean;
}

export interface FundingBlock {
  funding_estimated_annual_revenue: number;
  funding_actual_average_monthly_revenue: number;
  funding_when_funds_needed: number;
  funding_amount_requested: number;
  funding_use_of_funds: number;
  funding_products_requested?: string[];
}

export interface AccountsPayableBlock {
  accounts_payable_email_address: string;
  accounts_payable_first_name: string;
  accounts_payable_last_name: string;
  accounts_payable_payment_type: keyof IBillingInformation;
  accounts_payable_phone_number: string;
  accounts_payable_preferred_contact_method: keyof typeof CONTACT_METHODS;
  accounts_payable_preferred_invoicing_method: keyof typeof INVOICING_METHODS;
  accounts_payable_tax_exempt: boolean;
}

export interface BondingAgentBlock {
  bonding_agent_address: IAddress;
  bonding_agent_email_address: string;
  bonding_agent_first_name: string;
  bonding_agent_last_name: string;
  bonding_agent_company_name: string;
  bonding_agent_telephone: string;
}

export interface PrequalificationBlock {
  prequal_requested_amount: number;
  prequal_monthly_avg_revenue: string;
  prequal_time_in_business: string;
  prequal_prequalification_range: string;
  prequal_credit_score: string;
}

export interface BusinessBlock {
  business_legal_name: string;
  business_dba: string | null;
  business_stated_monthly_revenue: number;
  business_entity_type: number;
  business_start_date: string | null;
  business_state_of_incorporation: string | null;
  business_ein: string | null;
  business_bn: string | null;
  business_addresses: Array<IAddress | DeleteRecord>;
  business_phone_numbers: Array<PhoneNumber>;
}

export interface GuarantorBlock {
  guarantor_first_name: string | null;
  guarantor_last_name: string | null;
  guarantor_dob: string | null;
  guarantor_ssn: string | null;
  guarantor_email_address: string;
  guarantor_telephone: string;
  guarantor_address: IAddress | null;
}

export interface InsuranceBlock {
  insurance_certificate: string | null;
  insurance_address: IAddress | null;
  insurance_type: number | null;
  insurance_company: string | null;
  insurance_email_address: string | null;
  insurance_first_name: string | null;
  insurance_last_name: string | null;
  insurance_has_rental_protection: boolean;
  insurance_telephone: string | null;
}

export type PersonalTaxReturnBlock = {
  personal_information_id?: number;
  personal_tax_return_id?: number;
  personal_tax_return_information_taxpayer_first_name?: string | null;
  personal_tax_return_information_taxpayer_last_name?: string | null;
  personal_tax_return_information_taxpayer_ssn?: string | null;
  personal_tax_return_information_taxpayer_address_street?: string | null;
  personal_tax_return_information_taxpayer_address_city?: string | null;
  personal_tax_return_information_taxpayer_address_state?: string | null;
  personal_tax_return_information_taxpayer_address_zip_code?: string | null;
  personal_tax_return_information_spouse_first_name?: string | null;
  personal_tax_return_information_spouse_last_name?: string | null;
  personal_tax_return_information_spouse_ssn?: string | null;
  personal_tax_return_information_filing_status?: string | null;
  personal_tax_return_information_dependents?: TaxReturnDependent[] | null;
  personal_tax_return_information_income_wages_total?: number | null;
  personal_tax_return_information_income_taxable_interest?: number | null;
  personal_tax_return_information_income_ordinary_dividends?: number | null;
  personal_tax_return_information_income_qualified_dividends?: number | null;
  personal_tax_return_information_income_taxable_ira_distributions?:
    | number
    | null;
  personal_tax_return_information_income_taxable_pensions_annuities?:
    | number
    | null;
  personal_tax_return_information_income_taxable_social_security?:
    | number
    | null;
  personal_tax_return_information_income_capital_gain_loss?: number | null;
  personal_tax_return_information_income_other_income_loss?: number | null;
  personal_tax_return_information_income_total_income?: number | null;
  personal_tax_return_information_income_adjusted_gross_income?: number | null;
  personal_tax_return_information_income_standard_deduction?: number | null;
  personal_tax_return_information_tax_and_credits_child_tax_credit?:
    | boolean
    | null;
  personal_tax_return_information_tax_and_credits_self_employment_tax?:
    | number
    | null;
  personal_tax_return_information_tax_and_credits_total_tax?: number | null;
  personal_tax_return_information_payments_federal_tax_withheld_w2?:
    | number
    | null;
  personal_tax_return_information_payments_federal_tax_withheld_1099?:
    | number
    | null;
  personal_tax_return_information_payments_federal_tax_withheld_other?:
    | number
    | null;
  personal_tax_return_information_payments_estimated_tax_payments?:
    | number
    | null;
  personal_tax_return_information_payments_earned_income_credit?: number | null;
  personal_tax_return_information_payments_additional_child_tax_credit?:
    | number
    | null;
  personal_tax_return_information_payments_american_opportunity_credit?:
    | number
    | null;
  personal_tax_return_information_payments_total_payments?: number | null;
  personal_tax_return_information_refund_amount?: number | null;
  personal_tax_return_information_amount_owed?: number | null;
  personal_tax_return_information_estimated_tax_penalty?: number | null;
  personal_tax_return_information_refund_amount_applied_to_next_year?:
    | number
    | null;
  personal_tax_return_information_third_party_designee_name?: string | null;
  personal_tax_return_information_third_party_designee_phone?: string | null;
  personal_tax_return_information_third_party_designee_pin?: string | null;
  personal_tax_return_information_taxpayer_signature_date?: string | null;
  personal_tax_return_information_spouse_signature_date?: string | null;
  personal_tax_return_information_paid_preparer_name?: string | null;
  personal_tax_return_information_paid_preparer_ptin?: string | null;
  personal_tax_return_information_paid_preparer_self_employed?: string | null;
  personal_tax_return_information_paid_preparer_firm_name?: string | null;
  personal_tax_return_information_paid_preparer_phone_number?: string | null;
  personal_tax_return_information_paid_preparer_firm_ein?: string | null;
  personal_tax_return_information_paid_preparer_firm_address_street?:
    | string
    | null;
  personal_tax_return_information_paid_preparer_firm_address_city?:
    | string
    | null;
  personal_tax_return_information_paid_preparer_firm_address_state?:
    | string
    | null;
  personal_tax_return_information_paid_preparer_firm_address_zip_code?:
    | string
    | null;
  personal_tax_return_information_paid_preparer_signature_date?: string | null;
  personal_tax_return_information_year?: string | null;
};

export type BusinessTaxReturnBlock = {
  business_tax_return_information_id?: number;
  business_tax_return_year?: string;
  business_tax_return_corporation_name?: string;
  business_tax_return_employer_identification_number?: string;
  business_tax_return_date_incorporated?: string;
  business_tax_return_business_activity?: string;
  business_tax_return_product_or_service?: string;
  business_tax_return_shareholder_name?: string;
  business_tax_return_shareholder_identifying_number?: string;
  business_tax_return_current_year_allocation_percentage?: number;
  business_tax_return_form_8879_corp_tax_year?: string;
  business_tax_return_form_8879_corp_total_income?: number;
  business_tax_return_form_8879_corp_officer_signature_date?: string;
  business_tax_return_form_8879_corp_ero_signature_date?: string;
  business_tax_return_form_8879_corp_ero_efin?: string;
  business_tax_return_form_8879_corp_ero_pin?: string;
  business_tax_return_form_1120s_tax_year?: string;
  business_tax_return_form_1120s_selection_effective_date?: string;
  business_tax_return_form_1120s_total_assets?: number;
  business_tax_return_form_1120s_gross_receipts_or_sales?: number;
  business_tax_return_form_1120s_cost_of_goods_sold?: number;
  business_tax_return_form_1120s_gross_profit?: number;
  business_tax_return_form_1120s_net_gain_loss_from_form_4797?: number;
  business_tax_return_form_1120s_total_income_loss?: number;
  business_tax_return_form_1120s_ordinary_business_income_loss?: number;
  business_tax_return_form_1120s_total_deductions?: number;
  business_tax_return_form_1120s_other_deductions?: number;
  business_tax_return_form_1120s_depreciation?: number;
  business_tax_return_form_1120s_rents?: number;
  business_tax_return_form_1120s_repairs_and_maintenance?: number;
  business_tax_return_form_1120s_advertising?: number;
  business_tax_return_form_1120s_total_shares_outstanding?: string;
  business_tax_return_form_1120s_accounting_method?: string;
  business_tax_return_schedule_k1_ordinary_business_income_loss?: number;
  business_tax_return_schedule_k1_net_section_1231_gain_loss?: number;
  business_tax_return_schedule_k1_post_1986_depreciation_adjustment?: number;
  business_tax_return_schedule_k1_adjusted_gain_or_loss?: number;
  business_tax_return_schedule_k1_distributions?: number;
  business_tax_return_schedule_l_cash_beginning_of_year?: number;
  business_tax_return_schedule_l_cash_end_of_year?: number;
  business_tax_return_schedule_l_total_assets_beginning_of_year?: number;
  business_tax_return_schedule_l_total_assets_end_of_year?: number;
  business_tax_return_schedule_l_accounts_payable_beginning_of_year?: number;
  business_tax_return_schedule_l_accounts_payable_end_of_year?: number;
  business_tax_return_schedule_l_total_liabilities_and_equity_beginning_of_year?: number;
  business_tax_return_schedule_l_total_liabilities_and_equity_end_of_year?: number;
  business_tax_return_schedule_m1_net_income_loss_per_books?: number;
  business_tax_return_schedule_m1_income_loss_schedule_k?: number;
  business_tax_return_other_deductions_detail?: {
    type: string;
    amount: string;
  }[];
};

export interface TaxReturnDependent {
  first_name: string | null;
  last_name: string | null;
  relationship: string | null;
  social_security_number: string | null;
  child_tax_credit: boolean | null;
  credit_for_other_dependents: boolean | null;
}

export interface EquipmentDetails {
  equipment_details_equipment_location_address: string | null;
  equipment_details_model: string | null;
  equipment_details_serial_number: string | null;
  equipment_details_cost: number | null;
  equipment_details_year: number | null;
  equipment_details_miles_hours: string | null;
  equipment_details_seller: string | null;
  equipment_details_make: string | null;
  equipment_details_vendor: string | null;
  equipment_details_equipment_description: string | null;
  equipment_details_url_link: string | null;
  equipment_details_total_financing_request: number | null;
  equipment_details_requested_term: number | null;
  equipment_details_new_or_used: string | null;
  equipment_details_id: number;
}
export type EquipmentDetailsEntry = {
  id: keyof EquipmentDetails;
  titleKey: string;
  currency?: boolean;
};

export interface BusinessTransportation {
  business_transportation_information_authority_status_broker: string | null;
  business_transportation_information_authority_status_common: string | null;
  business_transportation_information_cargo_carried: string[] | null;
  business_transportation_information_usdot_number: string | null;
  business_transportation_information_entity_type: string | null;
  business_transportation_information_insurance_on_file: string | null;
  business_transportation_information_insurance_required: string | null;
  business_transportation_information_operation_classification: string[] | null;
  business_transportation_information_power_units: string | null;
  business_transportation_information_usdot_status: string | null;
}

export type BusinessTransportationEntry = {
  id: keyof BusinessTransportation;
  titleKey: string;
  dataType?: "string" | "array";
};

export interface WorkflowBlocksData {
  accounts_payable: AccountsPayableBlock;
  additional_business: AdditionalBusinessInfoFields;
  additional_owner: Array<OwnerAdditionalInfo>;
  affiliated_businesses: Array<AffiliatedBusinessInfoFields>;
  affiliated_businesses_additional_information: Array<AdditionalAffiliatedBusinessInfoFields>;
  affiliated_individuals: Array<IWFLAffiliatedIndividual>;
  affiliated_individuals_additional_information: Array<IWFLAffiliatedIndividualAdditional>;
  bank_account_details: Array<IBankAccountDetail>;
  bonding_agent: BondingAgentBlock;
  business: BusinessBlock;
  company_debts: Array<IDebt>;
  company_officers: Array<IOfficer>;
  customer_information: Array<IWorkflowLiveDebtor>;
  equipment_details: EquipmentDetails[];
  funding: FundingBlock;
  guarantor: GuarantorBlock;
  insurance: InsuranceBlock;
  personal: Array<OwnerWFL>;
  personal_tax_return_information: PersonalTaxReturnBlock[];
  prequal: PrequalificationBlock;
  trade_references: Array<TradeReference>;
  business_transportation_information: BusinessTransportation;
  personal_debt_schedule: Array<DebtSchedule>;
  business_debt_schedule: Array<DebtSchedule>;
  business_tax_return_information: BusinessTaxReturnBlock[];
}

export type IndexableWorkflowBlocksData = WorkflowBlocksData & {
  [key: string]: unknown;
};

export interface WorkflowTemplatesPayloadParams {
  active?: Booleanish;
  global?: Booleanish;
  category?: WorkflowCategory | WorkflowCategories;
  clients?: string[];
  approved?: Booleanish;
  products?: FinProductNum[];
  categories?: WorkflowCategories[];
  sort_by?: string;
}

export type WorkflowsViewFilters = {
  active?: Booleanish | null;
  approved?: Booleanish | null;
  billing_types?: BillingType[];
  categories?: number[];
  clients?: string[];
  global?: Booleanish;
  managed_by_lendflow?: Booleanish | null;
  only_global?: Booleanish | null;
  page?: number;
  per_page?: number;
  search?: string;
  sort_by?: string;
  workflow_segments?: string[];
  workflow_template_ids?: string[];
  selected_ids?: string[];
};

export type WorkflowCategory =
  | "credit_review"
  | "rental"
  | "data_collection"
  | "internal_funding"
  | "marketplace";

export type AffiliatedIndividualFieldMap = {
  [K in keyof typeof AFFILIATED_INDIVIDUAL_FIELD_IDS]: IWFLAffiliatedIndividual[(typeof AFFILIATED_INDIVIDUAL_FIELD_IDS)[K]];
};

export interface WorkflowEmailNotification {
  group: NotificationGroupType;
  name: string;
  stages?: ApplicationStage[];
  statuses?: DoStatus[];
  intervals?: string[];
  type: string;
}

export interface EmailNotification {
  group: string;
  name: string;
  emails: WorkflowEmailNotification[];
}

export interface EmailDefinition {
  name: string;
  group: string;
  emails: WorkflowEmailDefinition[];
}

export interface SmsDefinition extends Omit<EmailDefinition, "emails"> {
  sms_notifications: WorkflowEmailDefinition[];
}

export type WorkflowEmailDefinition = Omit<
  WorkflowEmailNotification,
  "stages"
> & {
  stages?: { name: string; value: string }[];
  events?: { name: string; value: string }[];
};

export interface NotificationItem {
  enabled?: boolean;
  group?: NotificationGroupType;
  template_id?: string | null;
  branding_id?: string | null;
  type?: string;
  branding?: number;
  name?: string;
  stage?: string;
  status?: string;
  interval?: string;
  event?: string;
}

export interface StageNameEntry {
  stage_name: string;
}

export interface ActivityHubOptions {
  active_tabs: CommunicationType[];
}

export type WebhookItem = WorkflowWebhookDefinitionItem & {
  urls: Array<string>;
  enabled: boolean;
  stageId?: IWorkflowStage["id"];
  description: string | null;
};

export interface WorkflowWebhookParameter {
  name: string;
  type: WorkflowWebhookParameterType;
  value: string | number | boolean;
}

export interface OptionalWorkflowWebhookParameter
  extends WorkflowWebhookParameter {
  enabled: boolean;
}

export type WebhookOptions = Record<string, Array<WebhookItem>>;
